import { runtimeConfig } from '@/runtime-config';
/**
 * Syncfusion Configuration
 * Fetches license key and service URL from backend API
 */

// ej2-base is large and only needed where Syncfusion components render. It is
// imported dynamically inside initializeSyncfusion() (below) so it stays out of the
// app's entry chunk rather than loading on every page.

export interface SyncfusionConfig {
  licenseKey: string;
  serviceUrl: string;
}

// Global config storage
let globalConfig: SyncfusionConfig | null = null;

/**
 * Fetch Syncfusion configuration from backend
 */
export const fetchSyncfusionConfig = async (): Promise<SyncfusionConfig | null> => {
  try {
    // const baseUrl = import.meta.env.VITE_API_BASE_URL || '';
    // use runtimeConfig for base URL
    // 🔥 FIXED: Changed from /api/config/syncfusion to /api/settings/syncfusion
    const response = await fetch(`${runtimeConfig.apiBaseUrl}/api/settings/syncfusion`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const result = await response.json();
    
    if (!result.success || !result.licenseKey) {
      throw new Error('Invalid configuration received from server');
    }

    return {
      licenseKey: result.licenseKey,
      serviceUrl: result.serviceUrl
    };
  } catch (error) {
    console.error('❌ Error fetching Syncfusion configuration:', error);
    return null;
  }
};

/**
 * Initialize Syncfusion with license key from backend
 * Call this once at app startup
 */
export const initializeSyncfusion = async (): Promise<SyncfusionConfig | null> => {
  try {
    // Fetch config from backend
    const config = await fetchSyncfusionConfig();

    if (!config || !config.licenseKey) {
      console.warn(
        '⚠️ Syncfusion license key not found in backend configuration.\n' +
        'Please ensure appsettings.json has Syncfusion:LicenseKey configured.\n' +
        'The application will show a license banner until this is fixed.'
      );
      return null;
    }

    // Register the license (ej2-base loaded on demand to keep it out of the entry chunk)
    const { registerLicense } = await import('@syncfusion/ej2-base');
    registerLicense(config.licenseKey);

    // Store globally for access by components
    globalConfig = config;
    
    return config;
  } catch (error) {
    console.error('❌ Failed to initialize Syncfusion:', error);
    return null;
  }
};

/**
 * Get the current Syncfusion configuration
 * Returns null if not yet initialized
 */
export const getSyncfusionConfig = (): SyncfusionConfig | null => {
  return globalConfig;
};

/**
 * Get the Syncfusion service URL.
 *
 * SECURITY: fails CLOSED. The Syncfusion PDF viewer POSTs document bytes to this service for
 * server-side processing, so we must NEVER fall back to Syncfusion's public demo host — that
 * would silently ship confidential tenant documents to a third party. Returns an empty string
 * when no self-hosted service URL is configured by the backend; callers must treat an empty
 * value as "viewer service unavailable" rather than leaking documents externally.
 */
export const getSyncfusionServiceUrl = (): string => {
  const url = globalConfig?.serviceUrl;
  if (!url) {
    console.error(
      '❌ Syncfusion service URL is not configured (Syncfusion:ServiceUrl). The PDF viewer ' +
      'server-side service is unavailable; refusing to fall back to the public demo host.'
    );
    return '';
  }
  return url;
};

/**
 * Default toolbar configuration for PDF Viewer
 */
export const defaultToolbarItems = [
  'PageNavigationTool',
  'MagnificationTool',
  'PanTool',
  'SelectionTool',
  'SearchOption',
  'PrintOption',
  'DownloadOption',
  'UndoRedoTool',
  'AnnotationEditTool',
  'CommentTool'
];

/**
 * Default annotation settings
 */
export const defaultAnnotationSettings = {
  author: 'Unknown User',
  isLock: false,
  minHeight: 0,
  minWidth: 0
};